import { useCallback, useMemo } from "react";

import type { QueryFilters } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";

import type { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { ALL_CAMPAIGNS_QUERY_PREFIX } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { getCampaignQueryKeyPrefix } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";

type RemoveOptions = {
    campaignId: string;
    queryTypesToKeep?: RankTrackerQueryType[];
};

export type RemoveCampaignQueriesFn = (options: RemoveOptions) => Promise<void>;

type UseCampaignQueryInvalidationReturn = {
    removeCampaignQueries: RemoveCampaignQueriesFn;
};

/**
 * React hook for invalidating or removing React Query cache entries for Rank
 * Tracker related queries.
 *
 * @returns An object with functions for invalidation and removal.
 */
export const useRankTrackerInvalidation = (): UseCampaignQueryInvalidationReturn => {
    const queryClient = useQueryClient();

    const removeCampaignQueries = useCallback(
        async (options: RemoveOptions): Promise<void> => {
            const { campaignId, queryTypesToKeep } = options;
            // Create prefix for queries that need to be invalidated when any
            // campaign-specific query changes, as they may contain aggregated
            // data or a list of campaigns, etc.
            const allCampaignsPrefix = getCampaignQueryKeyPrefix(ALL_CAMPAIGNS_QUERY_PREFIX);
            const hasSkip = queryTypesToKeep && queryTypesToKeep.length > 0;

            // When query types to keep are provided, we split queries into two groups:
            // 1. Queries to remove: those NOT in the keep list (permanently delete from cache)
            // 2. Queries to invalidate: those IN the keep list (mark as stale, refetch on next access)
            // This allows for optimistic updates of those keys that are invalidated.
            const removePredicate: QueryFilters["predicate"] = hasSkip
                ? ({ queryKey }) => !queryTypesToKeep.some((skipType) => queryKey.includes(skipType))
                : undefined;

            const invalidatePredicate: QueryFilters["predicate"] = hasSkip
                ? ({ queryKey }) => queryTypesToKeep.some((skipType) => queryKey.includes(skipType))
                : undefined;

            // Remove/invalidate queries for all campaigns first, as those
            // should be invalidated regardless of campaign id.
            queryClient.removeQueries({
                queryKey: allCampaignsPrefix,
                predicate: removePredicate,
            });

            await queryClient.invalidateQueries({
                queryKey: allCampaignsPrefix,
                predicate: invalidatePredicate,
            });

            if (campaignId !== ALL_CAMPAIGNS_QUERY_PREFIX) {
                // Create prefix for the specific campaign's queries
                const prefix = getCampaignQueryKeyPrefix(campaignId);

                // Remove/invalidate campaign-specific queries
                queryClient.removeQueries({
                    queryKey: prefix,
                    predicate: removePredicate,
                });

                await queryClient.invalidateQueries({
                    queryKey: prefix,
                    predicate: invalidatePredicate,
                });
            }

            return;
        },
        [queryClient],
    );

    return useMemo(
        () => ({
            /**
             * Removes React Query cache entries for campaigns  This deletes the
             * queries from the cache entirely. Queries for all campaigns (using
             * ALL_CAMPAIGNS_QUERY_PREFIX) are automatically removed/invalidated
             * as well.
             *
             * @param options - Configuration object with required campaignId and optional queryTypesToKeep.
             */
            removeCampaignQueries,
        }),
        [removeCampaignQueries],
    );
};
